'use client';

import { formatNumberToPriceText } from '@/lib/utils';
import { Product } from '@/models/Product';
import { ShoppingBag } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

/**
 * In-content product cards for blog posts.
 * Renders a horizontal scrollable row of product cards INSIDE the article body.
 * This breaks up long text and drives conversions from blog content.
 *
 * INJECTION SYSTEM:
 * This component is rendered when BlogReader encounters a marker element:
 *   <div data-hzc-products="true"></div>
 *
 * Auto-injection: Placed after the 4th paragraph by default (configurable).
 * Manual placement: CMS editors can place the marker anywhere in the content.
 *
 * TODO [BACKEND]: For manual product selection per injection point:
 *   <div data-hzc-products="true" data-product-ids="123,456,789"></div>
 *   The data-product-ids attribute should contain comma-separated product IDs
 *   chosen by the content editor in the CMS.
 *   BlogReader will read these IDs and filter the products array accordingly.
 *   Until then, the first 4 products from the blog's product list are shown.
 */

interface Props {
  products: Product[];
  maxItems?: number;
}

export default function InlineProductCards({ products, maxItems = 4 }: Props) {
  if (!products || products.length === 0) return null;

  const items = products.slice(0, maxItems);

  return (
    <div className="my-8 -mx-2 sm:-mx-4 not-prose">
      <div className="bg-gradient-to-r from-hzc_purple_50 to-purple-50 rounded-2xl p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag size={16} className="text-hzc_purple_500" />
          <span className="text-sm font-semibold text-gray-700">Önerilen Ürünler</span>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin">
          {items.map((product) => {
            const imgSrc = product.imageUrl || product.images?.[0]?.imageUrl;
            const hasDiscount = !!(
              product.priceBeforeDiscount && product.priceBeforeDiscount > product.price
            );
            const discountPct = hasDiscount
              ? Math.round(
                  ((product.priceBeforeDiscount! - product.price) / product.priceBeforeDiscount!) *
                    100,
                )
              : 0;

            return (
              <Link
                key={product.id}
                href={product.url.startsWith('/') ? product.url : `/${product.url}`}
                className="flex-shrink-0 w-[180px] sm:w-[200px] bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow snap-start group"
              >
                {/* Product image */}
                <div className="relative w-full aspect-[3/4] overflow-hidden bg-gray-50">
                  {imgSrc ? (
                    <Image
                      src={imgSrc}
                      alt={product.title}
                      fill
                      sizes="200px"
                      quality={60}
                      loading="lazy"
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag size={24} className="text-gray-300" />
                    </div>
                  )}
                  {hasDiscount && (
                    <span className="absolute top-2 left-2 z-10 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                      %{discountPct}
                    </span>
                  )}
                </div>

                {/* Product info */}
                <div className="p-2.5 flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-700 line-clamp-2 leading-tight group-hover:text-hzc_purple_500 transition-colors">
                    {product.title}
                  </span>
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    {hasDiscount && (
                      <span className="text-[10px] text-gray-400 line-through">
                        {formatNumberToPriceText(product.priceBeforeDiscount!)} TL
                      </span>
                    )}
                    <span className="text-sm font-bold text-hzc_purple_600">
                      {formatNumberToPriceText(product.price)} TL
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
